/**
 * Sign-in-with-Jubit OAuth popup flow.
 *
 * ADR-0008 Sprint 15 — replaces the Sprint 14 bridgeStore.connect() stub.
 *
 * Shape (mirrors Jubit's main-repo OAuth docs):
 *   1. Client generates a cryptographically random `state` (CSRF guard)
 *   2. Opens popup at `${JUBIT_OAUTH_URL}?client_id=jubuddy&state=...&redirect_uri=...`
 *   3. Server-side, Jubit authenticates the user and redirects to the provided
 *      `redirect_uri` with `?code=...&state=...` — this redirect page lives in
 *      main-Jubit and posts back to window.opener via postMessage
 *   4. This side listens for `message` events, validates:
 *        - event.origin matches JUBIT_ORIGIN (set at build time)
 *        - payload.state matches the state we sent (CSRF)
 *        - payload.token is a plausibly-shaped JWT string
 *   5. Returns { token, username } to the caller
 *
 * Failure modes:
 *   - Popup blocked → throws BridgeOAuthError('popup-blocked')
 *   - User closes popup → throws BridgeOAuthError('cancelled')
 *   - Origin mismatch → logs + rejects (possible phishing / XSS attempt)
 *   - State mismatch → rejects (CSRF)
 *   - Timeout (5 minutes) → rejects, popup is closed
 *
 * Endpoint wiring: Sprint 15 only introduces the CLIENT flow. The main-Jubit
 * /oauth/authorize endpoint lands in a separate PR on jubit-ai-universe.
 * Until that ships, calling `openJubitOAuth()` in a VITE_BRIDGE_URL-enabled
 * build opens a popup at a 404 URL and times out — the feature flag should
 * stay off in production until both sides are ready.
 */

export interface JubitOAuthResult {
  token: string;
  username: string;
}

export class BridgeOAuthError extends Error {
  constructor(
    public readonly code:
      | 'popup-blocked'
      | 'cancelled'
      | 'origin-mismatch'
      | 'state-mismatch'
      | 'malformed-payload'
      | 'timeout'
      | 'bridge-not-configured',
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'BridgeOAuthError';
  }
}

interface ResolvedConfig {
  oauthUrl: string;
  jubitOrigin: string;
}

function resolveConfig(): ResolvedConfig | null {
  try {
    const oauthUrl = import.meta.env.VITE_JUBIT_OAUTH_URL;
    const jubitOrigin = import.meta.env.VITE_JUBIT_ORIGIN;
    if (!oauthUrl || !jubitOrigin) return null;
    return { oauthUrl, jubitOrigin };
  } catch {
    return null;
  }
}

/**
 * Cryptographically random state string for CSRF guard.
 * Uses crypto.getRandomValues to produce 16 bytes → 32 hex chars.
 */
function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Basic JWT shape check — 3 base64url-ish segments. Not a signature verify. */
function looksLikeJwt(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  const parts = s.split('.');
  return parts.length === 3 && parts.every((p) => p.length > 0);
}

const POPUP_FEATURES = 'popup=yes,width=480,height=640,scrollbars=yes,resizable=no';
const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Opens the Jubit OAuth popup and resolves on postMessage with a valid
 * {state, token, username} payload. Rejects with BridgeOAuthError on any
 * failure path.
 */
export function openJubitOAuth(): Promise<JubitOAuthResult> {
  const config = resolveConfig();
  if (!config) {
    return Promise.reject(
      new BridgeOAuthError(
        'bridge-not-configured',
        'VITE_JUBIT_OAUTH_URL or VITE_JUBIT_ORIGIN unset',
      ),
    );
  }

  const state = generateState();
  const redirectUri = `${window.location.origin}/auth/callback`;
  const url = new URL(config.oauthUrl);
  url.searchParams.set('client_id', 'jubuddy');
  url.searchParams.set('state', state);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('scope', 'credits:write');

  const popup = window.open(url.toString(), 'jubit-oauth', POPUP_FEATURES);
  if (!popup) {
    return Promise.reject(new BridgeOAuthError('popup-blocked'));
  }

  return new Promise<JubitOAuthResult>((resolve, reject) => {
    let cleanedUp = false;

    function cleanup() {
      if (cleanedUp) return;
      cleanedUp = true;
      window.removeEventListener('message', onMessage);
      clearInterval(closedPoll);
      clearTimeout(timeoutHandle);
      if (popup && !popup.closed) popup.close();
    }

    function onMessage(event: MessageEvent) {
      // CSRF + phishing guard: reject anything not from the expected Jubit origin.
      if (event.origin !== config!.jubitOrigin) return; // silent ignore — not ours
      const data = event.data as {
        kind?: string;
        state?: string;
        token?: string;
        username?: string;
        error?: string;
      };
      if (!data || data.kind !== 'jubit-oauth-response') return;
      if (data.error) {
        cleanup();
        reject(new BridgeOAuthError('cancelled', data.error));
        return;
      }
      if (data.state !== state) {
        cleanup();
        reject(new BridgeOAuthError('state-mismatch', 'CSRF state mismatch'));
        return;
      }
      if (!looksLikeJwt(data.token) || typeof data.username !== 'string' || !data.username) {
        cleanup();
        reject(new BridgeOAuthError('malformed-payload'));
        return;
      }
      cleanup();
      resolve({ token: data.token, username: data.username });
    }

    // Detect user-closed-popup within 500ms granularity.
    const closedPoll = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new BridgeOAuthError('cancelled', 'user closed popup'));
      }
    }, 500);

    const timeoutHandle = setTimeout(() => {
      cleanup();
      reject(new BridgeOAuthError('timeout'));
    }, TIMEOUT_MS);

    window.addEventListener('message', onMessage);
  });
}
